import React from "react";
import { Card, CardContent, CardHeader } from "@/components/Card/Card";
import { MatchbackSalesData } from "../../api/fetchROIDashboardData/types";
import { MailIcon } from "lucide-react";
import { formatCurrency } from "@/modules/stochastic/features/DashboardPage/utils/chart";

interface Props {
  data?: MatchbackSalesData | null;
}

export const MatchbackSalesTable: React.FC<Props> = ({ data }) => {
  const items = data?.items || [];
  const totals = data?.totals || {};

  const formatNumber = (value: number | string) => {
    const num = Number(value);
    if (num === 0) return "—";
    return num.toLocaleString();
  };

  const formatCurrencyValue = (value: number | string) => {
    const num = Number(value);
    if (num === 0) return "—";
    return formatCurrency(num);
  };

  const years = Array.from(new Set(items.map((item) => item.year))).sort();

  const monthsMap = new Map<
    string,
    {
      month: string;
      monthNumber: number;
      data: Record<string, (typeof items)[0]>;
    }
  >();

  items.forEach((item) => {
    if (!monthsMap.has(item.month)) {
      monthsMap.set(item.month, {
        month: item.month,
        monthNumber: item.monthNumber,
        data: {},
      });
    }
    monthsMap.get(item.month)!.data[item.year] = item;
  });

  const rows = Array.from(monthsMap.values()).sort(
    (a, b) => a.monthNumber - b.monthNumber,
  );

  return (
    <Card className="w-full bg-white dark:bg-secondary-dark">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-green-50 dark:bg-green-900/20">
            <MailIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Matchback Sales
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sales from customers who received direct mail campaigns
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pb-10">
        <div className="w-full overflow-auto border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm">
          <table className="w-full caption-bottom text-sm min-w-max">
            <thead className="[&_tr]:border-b">
              <tr className="bg-gray-50/75 dark:bg-gray-800/50">
                <th className="h-11 px-4 align-middle text-muted-foreground text-left border-r border-gray-200 dark:border-gray-700 bg-gray-50/75 dark:bg-gray-800/50 font-semibold uppercase text-xs tracking-wider">
                  Month
                </th>
                {years.map((year, index) => (
                  <React.Fragment key={year}>
                    <th className="h-11 px-4 align-middle text-muted-foreground text-center border-r border-gray-200 dark:border-gray-700 bg-gray-50/75 dark:bg-gray-800/50 whitespace-nowrap font-semibold uppercase text-xs tracking-wider">
                      {year} Invoices
                    </th>
                    <th
                      className={`h-11 px-4 align-middle text-muted-foreground text-center border-gray-200 dark:border-gray-700 bg-gray-50/75 dark:bg-gray-800/50 whitespace-nowrap font-semibold uppercase text-xs tracking-wider ${
                        index === years.length - 1 ? "" : "border-r"
                      }`}
                    >
                      {year} Sales
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {rows.map((row) => (
                <tr
                  className="border-b border-gray-200 dark:border-gray-700 transition-colors hover:bg-muted/50"
                  key={row.month}
                >
                  <td className="px-4 py-3 text-left text-gray-700 dark:text-gray-300 border-r border-gray-200 dark:border-gray-700 whitespace-nowrap font-medium">
                    {row.month}
                  </td>
                  {years.map((year, index) => {
                    const yearData = row.data[year];
                    return (
                      <React.Fragment key={year}>
                        <td className="px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap border-r border-gray-200 dark:border-gray-700">
                          {formatNumber(yearData?.invoiceCount || 0)}
                        </td>
                        <td
                          className={`px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap ${
                            index === years.length - 1
                              ? ""
                              : "border-r border-gray-200 dark:border-gray-700"
                          }`}
                        >
                          {formatCurrencyValue(yearData?.totalSales || 0)}
                        </td>
                      </React.Fragment>
                    );
                  })}
                </tr>
              ))}
              {/* Grand Total Row */}
              <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
                <td className="px-4 py-3 text-left text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-700 whitespace-nowrap">
                  Grand Total
                </td>
                {years.map((year, index) => {
                  const yearTotal = totals[year];
                  return (
                    <React.Fragment key={year}>
                      <td className="px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap border-r border-gray-200 dark:border-gray-700">
                        {formatNumber(yearTotal?.invoiceCount || 0)}
                      </td>
                      <td
                        className={`px-4 py-3 text-right text-gray-900 dark:text-white whitespace-nowrap ${
                          index === years.length - 1
                            ? ""
                            : "border-r border-gray-200 dark:border-gray-700"
                        }`}
                      >
                        {formatCurrencyValue(yearTotal?.totalSales || 0)}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
